import { Alert, AlertTitle, Box, Button, ButtonGroup, CircularProgress, Divider, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DISEASE_RISK_MODELS, DOWNY_MODEL, MELCAST_MODEL, POWDERY_MODEL } from "../../config/diseaseRisk.config";
import { cancelDiseaseRisk, fetchDiseaseRiskStart } from "../../store/features/customer/diseaseRisk/diseaseRiskSlice";
import { formatRiskDate, getDefaultRiskCultivation, getRiskBounds, getRiskCultivations, getRiskPeriod, getRiskRequestKey, validateRiskRequest } from "../../utils/diseaseRiskUtils";
import { resolveFieldUuid } from "../../utils/fieldDataDiseaseStatusUtils";
import { RiskDailyChart, RiskHistoryChart } from "./diseaseRiskCharts.component";
import DiseaseRiskDetail from "./diseaseRiskDetail.component";

const EMPTY_STATE = { status: "idle", data: null, error: null };

const RiskModelWorkspace = ({ crop, fieldUuid, enabled }) => {
  const dispatch = useDispatch();
  const [model, setModel] = useState(crop.riskCrop?.models[0] || "");
  const [period, setPeriod] = useState(() => ({ ...getRiskPeriod(crop), seasonStartDate: crop.start_date || "" }));
  const [preset, setPreset] = useState(30);
  const [selection, setSelection] = useState(null);
  const { from, to, seasonStartDate } = period;
  const melonType = crop.riskCrop?.melonType;
  // Depend on parameter values so parent/store renders and preset-only changes
  // cannot restart a request with the same query.
  const request = useMemo(() => ({
    fieldUuid, model, from, to, seasonStartDate, melonType,
  }), [fieldUuid, model, from, to, seasonStartDate, melonType]);
  const requestKey = getRiskRequestKey(request);
  const state = useSelector((store) => store.diseaseRisk?.key === requestKey ? store.diseaseRisk : EMPTY_STATE);
  const validation = validateRiskRequest(request, crop);
  const bounds = getRiskBounds(crop);
  const result = state.data;
  const selected = result?.assessments.find((item) => selection?.key === requestKey && item.date === selection.date) || result?.assessments[result.assessments.length - 1];
  const selectDay = (date) => setSelection({ key: requestKey, date });

  useEffect(() => {
    if (!enabled || validation) return undefined;
    let disposed = false;
    let started = false;
    // React StrictMode replays mounting effects. Defer dispatch until the
    // replay has settled so it cannot launch a duplicate recovery batch.
    queueMicrotask(() => {
      if (disposed) return;
      started = true;
      dispatch(fetchDiseaseRiskStart(request));
    });
    return () => {
      disposed = true;
      if (started) dispatch(cancelDiseaseRisk());
    };
  }, [dispatch, enabled, request, validation]);

  const changeDate = (key, value) => {
    setPreset("custom");
    setPeriod((current) => current[key] === value ? current : { ...current, [key]: value });
  };

  if (!crop.riskCrop) return <Alert severity="info">Nessun modello disponibile per questa coltura.</Alert>;

  return <Stack spacing={3}>
    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
      {crop.riskCrop.models.length > 1 ? <TextField select size="small" label="Modello" value={model} onChange={(event) => setModel(event.target.value)} sx={{ minWidth: 260 }}>
        {crop.riskCrop.models.map((key) => <MenuItem key={key} value={key}>{DISEASE_RISK_MODELS[key].label}</MenuItem>)}
      </TextField> : <Box sx={{ minWidth: 260 }}><Typography variant="caption" color="text.secondary">Modello</Typography><Typography fontWeight={600}>{DISEASE_RISK_MODELS[model].label}</Typography></Box>}
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Indici e condizioni del modello · dati storici</Typography>
    </Stack>
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" useFlexGap
        sx={{ "& .MuiButtonGroup-root, & .MuiInputBase-root": { height: 40 } }}>
        <ButtonGroup size="small" aria-label="Periodo del rischio">
          {[7, 30].map((days) => <Button key={days} variant={preset === days ? "contained" : "outlined"} aria-pressed={preset === days}
            onClick={() => { setPreset(days); setPeriod((current) => ({ ...current, ...getRiskPeriod(crop, days) })); }}>{days} giorni</Button>)}
          <Button variant={preset === "custom" ? "contained" : "outlined"} aria-pressed={preset === "custom"} onClick={() => setPreset("custom")}>Personalizzato</Button>
        </ButtonGroup>
        <TextField label="Dal" type="date" size="small" value={from} onChange={(event) => changeDate("from", event.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: bounds.min, max: bounds.max } }} />
        <TextField label="Al" type="date" size="small" value={to} onChange={(event) => changeDate("to", event.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: bounds.min, max: bounds.max } }} />
        {model === MELCAST_MODEL && <TextField label="Inizio stagione del modello" type="date" size="small" value={seasonStartDate}
          onChange={(event) => changeDate("seasonStartDate", event.target.value)} sx={{ minWidth: 235 }}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to } }} />}
      </Stack>
      {model === MELCAST_MODEL && <Typography variant="caption" color="text.secondary">L’inizio stagione modifica solo questo calcolo. La cumulata conserva l’accumulo stagionale anche visualizzando un periodo più breve.</Typography>}
      {validation && <Alert severity="warning">{validation}</Alert>}
    </Stack>
    {state.status === "loading" && <Stack direction="row" gap={1.5} alignItems="center" role="status"><CircularProgress size={22} /><Typography variant="body2">Caricamento delle valutazioni del modello…</Typography></Stack>}
    {state.error && <Alert severity="error"><AlertTitle>{state.error.message}</AlertTitle>{state.error.detail}</Alert>}
    {result && <>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
        <Box>
          <Typography component="h3" fontSize={18} fontWeight={600}>{model === POWDERY_MODEL ? "Severità cumulata · finestra mobile di 6 giorni" : model === DOWNY_MODEL ? "Condizioni favorevoli a infezione e germinazione" : "Indice di favorevolezza ambientale · MELCAST"}</Typography>
          <Typography variant="body2" color="text.secondary">Periodo applicato: {formatRiskDate(from)} – {formatRiskDate(to)}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" role="status">{result.assessments.length}/{result.expectedCount} {model === MELCAST_MODEL ? "giorni disponibili" : "valutazioni disponibili"}</Typography>
      </Stack>
      {result.missingDates.length > 0 && <Alert severity="warning">{result.missingDates.length} {model === MELCAST_MODEL ? "giorni senza record" : "valutazioni non disponibili per dati meteo incompleti o assenti"}. I giorni mancanti sono indicati da interruzioni nel grafico.</Alert>}
      {model === DOWNY_MODEL && <Typography variant="body2" color="text.secondary">SIP: periodo di sporulazione e infezione. I flag indicano condizioni favorevoli secondo il modello, non una diagnosi. Le ore mancanti sono trattate dal modello come assenza di bagnatura.</Typography>}
      {selected ? <>
        <RiskHistoryChart data={result.points} model={model} selectedDate={selected.date} onSelect={selectDay} efiInterval={result.metadata?.efi_interval} />
        {model === MELCAST_MODEL && <><Typography component="h3" fontSize={16} fontWeight={600}>EFI giornaliero</Typography><RiskDailyChart data={result.points} melcast /></>}
        <Divider />
        <DiseaseRiskDetail result={result} selected={selected} model={model} onSelect={selectDay} />
      </> : <Typography color="text.secondary">Nessuna valutazione disponibile per il periodo selezionato. Prova un altro intervallo.</Typography>}
    </>}
  </Stack>;
};

const DiseaseRiskPanel = ({ field, enabled }) => {
  const cultivations = useMemo(() => getRiskCultivations(field), [field]);
  const [selectedKey, setSelectedKey] = useState("");
  const selected = cultivations.find((crop) => crop.key === selectedKey) || getDefaultRiskCultivation(cultivations);
  const fieldUuid = resolveFieldUuid(field);
  // A saved cultivation can change crop or season while keeping the same id.
  // Reset local selections only when these values change, not on store renders.
  const workspaceKey = JSON.stringify([
    fieldUuid, selected?.key, selected?.colture?.id, selected?.name,
    selected?.start_date, selected?.end_date,
  ]);
  return <Paper elevation={0} sx={{ p: 3, minWidth: 0 }}>
    <Stack spacing={3}>
      <Box>
        <Typography component="h2" fontSize={20} fontWeight={600}>Rischio fitosanitario</Typography>
        <Typography variant="body2" color="text.secondary">Consulta l’andamento dei modelli e i dati utilizzati per ogni valutazione.</Typography>
      </Box>
      {!field ? <Typography role="status">Caricamento del terreno…</Typography> : !selected ? <Alert severity="info">Nessuna coltivazione registrata per questo terreno.</Alert> : <>
        <TextField select label="Coltivazione / stagione" size="small" value={selected.key} onChange={(event) => setSelectedKey(event.target.value)} sx={{ width: { xs: "100%", lg: 480 } }}>
          {cultivations.map((crop) => <MenuItem key={crop.key} value={crop.key}>{crop.name} · {formatRiskDate(crop.start_date)} – {crop.end_date ? formatRiskDate(crop.end_date) : "in corso"}</MenuItem>)}
        </TextField>
        <RiskModelWorkspace key={workspaceKey} crop={selected} fieldUuid={fieldUuid} enabled={enabled} />
      </>}
    </Stack>
  </Paper>;
};

export default DiseaseRiskPanel;
